import React, { useEffect, useMemo, useRef, useState } from "react";
import Input from "../../components/Input/Input";
import { useSearchParams } from "react-router-dom";
import AccessModuleService from "../../services/AccessModuleService";
import { v4 as uuidv4 } from "uuid";
import { generatePastelRGBColor } from "../../utils/color";
import { publish } from "../../utils/events";
import { SegmentTooltip } from "./SegmentTooltip";
import { clamp } from "../../utils/math";
import { LineTimeline } from "./LineTimeline";
import { MdOutlineDelete } from "react-icons/md";
import { SelectAccessModule } from "../../components/SelectAccessModule/SelectAccessModule";
import Button from "../../components/Button/Button";
import { AccessIcons } from "../../utils/accessIcons";
import { LuDoorOpen, LuLock } from "react-icons/lu";
import { ImConnection } from "react-icons/im";
import { ModuleIps, ModuleType } from "../../enums/moduleType";
import { searchParamsToObject } from "../../utils/url";
import { useUserStore } from "../../contexts/userStore";
import { useSSEContext } from "../../contexts/sse";

export const MAX_HOURS = 24;
export const NAME_COL_WIDTH = 200;

export function AccessModulesHistoric({ ...props }) {
   const ref = useRef();

   const { selectedCondominio } = useUserStore();
   const [searchParams, setSearchParams] = useSearchParams();
   const [metricWidthMultiplier, setMetricWidthMultiplier] = useState(1);
   const [modules, setModules] = useState([]);
   const [events, setEvents] = useState({});
   const [accessModule, setAccessModule] = useState();
   const { sseReceived } = useSSEContext();

   const modulesUuids = searchParams.get('m')?.split(',') ?? [];
   const startDate = useMemo(() => searchParams.get('sdt') ? new Date(Number(searchParams.get('sdt'))) : new Date(Date.now() - 1000 * 60 * 60 * 24 * 2), [searchParams]);
   const endDate = useMemo(() => searchParams.get('edt') ? new Date(Number(searchParams.get('edt'))) : new Date(), [searchParams]);
   const initialDate = useMemo(() => new Date(Math.floor(startDate.getTime() / (1000 * 60 * 60)) * 1000 * 60 * 60), [startDate]);
   const finalDate = useMemo(() => new Date(Math.ceil(endDate.getTime() / (1000 * 60 * 60)) * 1000 * 60 * 60), [endDate]);
   const hoursInRange = Math.ceil(endDate.getTime() / (1000 * 60 * 60)) - Math.floor(startDate.getTime() / (1000 * 60 * 60));

   async function fetchModules() {
      const uuids = modulesUuids.map(uuid => uuid.split(':')[0]);
      const { data } = await AccessModuleService.getByUuids(uuids);
      if (data) {
         setModules(data);
      }
   }

   function setStartDate(date) {
      setSearchParams({
         ...searchParamsToObject(searchParams),
         sdt: new Date(date).getTime()
      });
   }

   function setEndDate(date) {
      setSearchParams({
         ...searchParamsToObject(searchParams),
         edt: new Date(date).getTime()
      });
   }

   useEffect(() => {
      if (!modulesUuids) return;
      fetchModules();
   }, [
      searchParams
   ]);

   useEffect(() => {
      if (!ref.current) return;
      const onWheel = (e) => {
         if (e.ctrlKey) {
            e.preventDefault();
            const deltaY = e.deltaY;
            setMetricWidthMultiplier(prev => clamp(0.6, 3, deltaY > 0 ? prev - 0.1 : prev + 0.1));
         }
      }
      ref.current.addEventListener('wheel', onWheel, { passive: false });
      return () => ref.current?.removeEventListener('wheel', onWheel);
   }, [ref.current]);

   async function fetchEvents() {
      const payload = {
         startDate: startDate.toISOString(),
         endDate: endDate.toISOString(),
         modules: modulesUuids.map(uuidWithIndex => {
            const [uuid, index] = uuidWithIndex.split(':');
            return {
               uuid,
               index: index != null ? Number(index) : null,
            };
         }),
      };

      const { data: events } = await AccessModuleService.listStatesByCondominium(selectedCondominio, payload);
      if (events && Array.isArray(events)) {
         setEvents(prev => {
            const newEvents = {};
            for (const event of events) {
               const moduleUuid = `${event.moduleUuid}${event.accessIndex != null ? `:${event.accessIndex}` : ''}`;
               if (!newEvents[moduleUuid]) {
                  newEvents[moduleUuid] = [];
               }
               newEvents[moduleUuid].push({
                  ...event,
                  startAt: new Date(event.startAt),
                  endAt: event.endAt ? new Date(event.endAt) : null,
               });
            }
            return newEvents;
         });
      }
   }

   useEffect(() => {
      if (sseReceived) {
         if (['moduleState', 'moduleConnection'].includes(sseReceived.action)) {
            const { moduleUuid, accessIndex, state, connection, startAt, endAt } = sseReceived.content;
            const moduleKey = `${moduleUuid}${accessIndex != null ? `:${accessIndex}` : ''}`;
            setEvents(prev => {
               const newEvents = { ...prev };
               if (!newEvents[moduleKey]) {
                  newEvents[moduleKey] = [];
               }
               let newEvent = {
                  moduleUuid,
                  accessIndex,
                  startAt: new Date(startAt),
                  endAt: endAt ? new Date(endAt) : null,
               };
               if (sseReceived.action === 'moduleState') {
                  newEvent.state = state;
               } else {
                  newEvent.connection = connection;
               }
               newEvents[moduleKey].push(newEvent);
               return newEvents;
            });
         }
      }
   }, [sseReceived]);

   useEffect(() => {
      fetchEvents();
   }, [
      startDate,
      endDate,
      modules,
      selectedCondominio
   ]);

   function mountSegments(moduleEvents) {
      let connectionSegments = [];
      let stateSegments = [];

      if (!moduleEvents) {
         return {
            connectionSegments,
            stateSegments
         }
      }

      let initialX = initialDate.getTime();
      let finalX = finalDate.getTime();
      let widthHourColumn = calculateHourWidth();

      let lastConnEvent = null;
      let lastStEvent = null;

      for (let i = 0; i < moduleEvents.length; i++) {
         const event = moduleEvents[i];
         const eventDate = event.startAt.getTime();

         if (event.connection) {
            if (lastConnEvent && lastConnEvent.endDate === null && lastConnEvent.connection !== event.connection) {
               connectionSegments.push({
                  id: lastConnEvent.id,
                  offsetX: widthHourColumn * ((lastConnEvent.startDate - initialX) / (1000 * 60 * 60)),
                  startDate: lastConnEvent.startDate,
                  endDate: eventDate,
                  width: widthHourColumn * ((eventDate - lastConnEvent.startDate) / (1000 * 60 * 60)),
                  connection: lastConnEvent.connection,
               });
               lastConnEvent = {
                  id: event.id,
                  connection: event.connection,
                  startDate: eventDate,
                  endDate: null,
                  width: 0,
               };
            }
            if (!lastConnEvent || lastConnEvent.endDate) {
               lastConnEvent = {
                  id: event.id,
                  connection: event.connection,
                  startDate: eventDate,
                  endDate: null,
                  width: 0,
               };
            }
         }

         if (event.state) {
            if (lastStEvent && lastStEvent.endDate === null && lastStEvent.state !== event.state) {
               stateSegments.push({
                  id: lastStEvent.id,
                  offsetX: widthHourColumn * ((lastStEvent.startDate - initialX) / (1000 * 60 * 60)),
                  startDate: lastStEvent.startDate,
                  endDate: eventDate,
                  width: widthHourColumn * ((eventDate - lastStEvent.startDate) / (1000 * 60 * 60)),
                  state: lastStEvent.state,
               });
               lastStEvent = {
                  id: event.id,
                  state: event.state,
                  startDate: eventDate,
                  endDate: null,
                  width: 0,
               };
            }
            if (!lastStEvent || lastStEvent.endDate) {
               lastStEvent = {
                  id: event.id,
                  state: event.state,
                  startDate: eventDate,
                  endDate: null,
                  width: 0,
               };
            }
         }
      }

      if (lastConnEvent && lastConnEvent.endDate === null) {
         connectionSegments.push({
            id: lastConnEvent.id,
            offsetX: widthHourColumn * ((lastConnEvent.startDate - initialX) / (1000 * 60 * 60)),
            startDate: lastConnEvent.startDate,
            endDate: endDate.getTime(),
            width: widthHourColumn * ((endDate.getTime() - lastConnEvent.startDate) / (1000 * 60 * 60)),
            connection: lastConnEvent.connection,
         });
      }

      if (lastStEvent && lastStEvent.endDate === null) {
         stateSegments.push({
            id: lastStEvent.id,
            offsetX: widthHourColumn * ((lastStEvent.startDate - initialX) / (1000 * 60 * 60)),
            startDate: lastStEvent.startDate,
            endDate: endDate.getTime(),
            width: widthHourColumn * ((endDate.getTime() - lastStEvent.startDate) / (1000 * 60 * 60)),
            state: lastStEvent.state,
         });
      }

      return {
         connectionSegments,
         stateSegments,
      }
   }

   function formatDate(date) {
      return new Intl.DateTimeFormat('sv-SE', {
         year: 'numeric',
         month: '2-digit',
         day: '2-digit',
         hour: '2-digit',
         minute: '2-digit',
         hour12: false
      }).format(date).replace(' ', 'T');
   }

   function mountHead() {
      let content = [];
      let hour = endDate.getHours();
      for (let i = 0; i < hoursInRange; i++) {
         content = [
            <span className="relative w-(--widthHourColumn) h-fit text-left box-border!">
               <span className="absolute h-[8px] w-px -top-3 left-0 bg-(--borderColor)"></span>
               <span className="absolute h-[4px] w-px -top-3 left-1/2 bg-(--borderColor)"></span>
               <span className="inline-block relative -translate-x-1/2 text-[0.7rem]! font-extrabold! opacity-60">
                  {hour}h
               </span>
            </span>,
            ...content,
         ]
         hour = hour - 1 === -1 ? 23 : hour - 1;
      }
      return content;
   }

   function calculateHourWidth() {
      if (!ref.current) return;
      const containerRect = ref.current.getBoundingClientRect();
      if (hoursInRange > MAX_HOURS) {
         return (containerRect.width - NAME_COL_WIDTH) * metricWidthMultiplier / MAX_HOURS;
      }
      return (containerRect.width - NAME_COL_WIDTH) * metricWidthMultiplier / hoursInRange;
   }

   useEffect(() => {
      if (!ref.current) return;
      ref.current.scrollLeft = ref.current.scrollWidth;
   }, [
      ref.current,
      events,
   ]);

   const segments = useMemo(() => {
      const segs = {};
      for (const moduleUuid of modulesUuids) {
         segs[moduleUuid] = mountSegments(events[moduleUuid]);
      }
      return segs;
   }, [events, metricWidthMultiplier]);

   function onSegmentMouseEnter(segmentData, targetEl) {
      publish(`segmentHover`, { data: segmentData, el: targetEl });
   }

   function onSegmentMouseLeave(segmentData, targetEl) {
      publish(`segmentHover`, null);
   }

   function removeModule(uuid) {
      const upUuids = modulesUuids.filter(m => m !== uuid).join(',');
      setSearchParams({
         ...searchParamsToObject(searchParams),
         m: upUuids
      });
   }

   function addModule(uuid) {
      const set = new Set(modulesUuids);
      set.add(uuid);
      setSearchParams({
         ...searchParamsToObject(searchParams),
         m: Array.from(set).join(',')
      });
   }

   const modulesWithIndex = useMemo(() => {
      if (!modules || modules.length === 0) return [];
      return modulesUuids.map(moduleUuid => {
         const [uuid, index] = moduleUuid.split(':');
         const _module = modules.find(m => m.uuid === uuid);
         if (!_module) return null;
         return {
            ..._module,
            index,
            name: _module?.[_module.type === ModuleType.IpRfReceiverModule ? 'garageIp' : 'garage']?.[`name${Number(index) + 1}`] || _module.name,
         };
      }).filter(m => m !== null);
   }, [modulesUuids, modules]);

   return (
      <div style={{
         '--nameColumnWidth': `${NAME_COL_WIDTH}px`,
         '--widthHourColumn': `${calculateHourWidth()}px`
      }}>
         <p className="text-(--textColor)">Histórico dos estados de conexão e abertura dos módulos de acessos.</p>
         <div className="w-full mt-4! flex justify-between flex-row items-center">
            <div className="flex flex-row items-center gap-3.5 w-fit">
               <Input
                  name="startDatetime"
                  placeholder="Início"
                  value={formatDate(startDate)}
                  type="datetime-local"
                  autoComplete="off"
                  onChange={({ target }) => setStartDate(new Date(target.value))}
               />
               <Input
                  name="startDatetime"
                  placeholder="Início"
                  value={formatDate(endDate)}
                  type="datetime-local"
                  autoComplete="off"
                  onChange={({ target }) => setEndDate(new Date(target.value))}
               />
            </div>
            <div className="flex flex-row gap-3.5">
               <div className="w-[220px]">
                  <SelectAccessModule
                     labelText={false}
                     useUuid={true}
                     accessModule={accessModule}
                     onChange={value => setAccessModule(value)}
                  />
               </div>
               <Button
                  action={"add"}
                  label={"Adicionar acesso"}
                  className={"button-blue"}
                  onClick={() => {
                     addModule(accessModule);
                  }}
               />
            </div>
         </div>
         {modules && modules.length > 0 && (
            <div ref={ref} className="w-full flex flex-row overflow-x-auto overflow-y-visible mt-3! box-border! bborder! border-(--borderColor)! rounded-lg">
               <div className="w-(--nameColumnWidth) sticky z-20 pp-3! box-border! left-0 bg-(--bodyBackgroundColor) shadow-(--box-shadow-lg)">
                  <div className="w-(--nameColumnWidth) sticky left-0 h-[29px] box-border py-1.5! text-(--textColor) font-extrabold! text-[0.65rem]! uppercase opacity-60">
                     Pontos de Acessos
                  </div>
                  {modulesWithIndex.map((_module, i) => (
                     <div
                        className="relative text-(--textColor) box-border! pr-3! py-2! h-[50px]! mb-3! flex items-center bgg-(--bgRColor) group"
                        style={{ '--bgRColor': generatePastelRGBColor(5, 10) }}
                        key={_module.uuid + _module.index}
                     >
                        {i > 0 && (
                           <span className="absolute w-full h-px -top-[0.41rem] left-0 bg-(--borderColor)"></span>
                        )}
                        {i < (modulesWithIndex.length - 1) && (
                           <span className="absolute w-full h-px -bottom-[0.41rem] left-0 bg-(--borderColor)"></span>
                        )}
                        <span className="relative w-full flex justify-between items-center gap-2 z-20 font-extrabold!">
                           <span className='flex items-center gap-3'>
                              <span className='text-[1rem]!'>{AccessIcons[_module.type]}</span>
                              {_module.name}
                           </span>
                           <button
                              type="button"
                              className="opacity-0 group-hover:opacity-60 hover:opacity-80 duration-200 cursor-pointer"
                              onClick={e => removeModule(_module.uuid + (_module.index ? ':' + _module.index : ''))}
                           >
                              <MdOutlineDelete size={16} />
                           </button>
                           <div className="flex flex-col items-center gap-[0.74rem] opacity-40 text-(--textColor)">
                              {ModuleIps.includes(_module.type) && (
                                 <ImConnection size={14} />
                              )}
                              {![ModuleType.TurnstileModule, ModuleType.IpTurnstileModule, ModuleType.KioskIpModule].includes(_module.type) && (
                                 <LuLock size={14} />
                              )}
                           </div>
                        </span>
                     </div>
                  ))}
               </div>
               <div className="relative w-fit flex p-3! box-border! flex-col z-10 text-(--textColor)">
                  <div className="flex w-fit flex-row mb-[5px]">
                     {mountHead()}
                  </div>
                  <div className="mt-[0.85rem]!">
                     {modulesWithIndex.map(_module => (
                        <React.Fragment key={_module.uuid + _module.index}>
                           <div className="relative flex flex-row items-center h-[50px] bgg-(--bgRColor) mb-3!" style={{ '--bgRColor': generatePastelRGBColor(88, 95) }}>
                              {ModuleIps.includes(_module.type) && (
                                 <>
                                    {segments[`${_module.uuid}${_module.index ? `:${_module.index}` : ''}`]?.connectionSegments.map((segment, i) => (
                                       <div
                                          key={segment.id}
                                          className={`absolute group border-t! border-(--bodyBackgroundColor)! top-0 w-(--segmentWidth) left-(--offsetX) ${![ModuleType.TurnstileModule, ModuleType.IpTurnstileModule].includes(_module.type) ? 'h-1/2' : 'h-full'} ${segment.connection === 'connected' ? 'bg-(--onlineColor)' : 'bg-(--offlineColor)'} hover:opacity-60 duration-200`}
                                          style={{
                                             '--segmentWidth': `${segment.width}px`,
                                             '--offsetX': `${segment.offsetX}px`
                                          }}
                                          onMouseEnter={e => onSegmentMouseEnter(segment, e.target)}
                                          onMouseLeave={e => onSegmentMouseLeave(segment, e.target)}
                                       >
                                       </div>
                                    ))}
                                 </>
                              )}
                              {![ModuleType.TurnstileModule, ModuleType.IpTurnstileModule, ModuleType.KioskIpModule].includes(_module.type) && (
                                 <>
                                    {segments[`${_module.uuid}${_module.index ? `:${_module.index}` : ''}`]?.stateSegments.map((segment, i) => (
                                       <div
                                          key={segment.id}
                                          className={`absolute group border-t! border-(--bodyBackgroundColor)! ${ModuleIps.includes(_module.type) ? 'top-1/2' : 'top-0'} w-(--segmentWidth) left-(--offsetX) ${ModuleIps.includes(_module.type) ? 'h-1/2' : 'h-full'} ${segment.state === 'open' ? 'bg-(--onlineColor)' : 'bg-(--offlineColor)'} hover:opacity-60 duration-200`}
                                          style={{
                                             '--segmentWidth': `${segment.width}px`,
                                             '--offsetX': `${segment.offsetX}px`
                                          }}
                                          onMouseEnter={e => onSegmentMouseEnter(segment, e.target)}
                                          onMouseLeave={e => onSegmentMouseLeave(segment, e.target)}
                                       >
                                       </div>
                                    ))}
                                 </>
                              )}
                           </div>
                        </React.Fragment>
                     ))}
                  </div>
               </div>
            </div>
         )}
         <SegmentTooltip timelineContainerRef={ref} />
         <LineTimeline
            widthCol={calculateHourWidth()}
            timelineContainerRef={ref}
            startDate={initialDate}
            endDate={finalDate}
         />
      </div>
   );
}